import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import React, { createContext, PropsWithChildren, useContext } from 'react';
import { ConfirmProvider } from './confirm';
import { PromptProvider } from './prompt';

const PortalContainerContext = createContext<HTMLElement | null>(null);

/**
 * Mounts kit overlays (menus, tooltips, popovers, dialogs, sheets, selects) inside the themed
 * `.forge-root` instead of `document.body`, where the theme tokens do not resolve. Every shell
 * that renders `.forge-root` provides it; overlay content renders nothing until the container
 * exists. `Tooltip` is the exception: Radix throws without a `TooltipProvider`, and the kit keeps
 * a single one here so tooltips share a delay group, so kit consumers must mount under a shell or
 * `renderInForgeRoot` in tests. The same tree hosts the `useConfirm` and `usePrompt` dialogs.
 */
export const UiProvider: React.FC<PropsWithChildren<{ container: HTMLElement | null }>> = ({
  children,
  container,
}) => (
  <PortalContainerContext.Provider value={container}>
    <TooltipPrimitive.Provider delayDuration={300} skipDelayDuration={150}>
      <ConfirmProvider>
        <PromptProvider>{children}</PromptProvider>
      </ConfirmProvider>
    </TooltipPrimitive.Provider>
  </PortalContainerContext.Provider>
);

export function usePortalContainer(): HTMLElement | null {
  return useContext(PortalContainerContext);
}
